import React from 'react';
import { Container } from '@zillow/zrs-comet';
import Page from '@common/components/Page';
import InfoSection from './modules/InfoSection';
import LedgerSection from './modules/LedgerSection';
import RolloutModal from './modules/RolloutModal';
import { LANDLORD_STATUS } from '@rental-manager/constants/OnboardingConstants';
import userPersonas from '@common/constants/userPersonas';
import DismissibleActionConstants from '@common/constants/DismissibleActionConstants';
import './index.scss';
import type { IBannerModel, IFeaturePermissionObject, ILeasesForPropertyModel } from '@rental-manager/models/Types';
import { COVERAGE_REQUEST_SIGNED } from '@rental-manager/constants/RentalProtectionPolicyStatusConstants';

type AdditionalLeaseSelectorProps = {
  index: number | null;
};
type LeaseSelectorProps = ILeasesForPropertyModel & AdditionalLeaseSelectorProps;

type Props = {
  allTransactions?: any;
  banners: Array<IBannerModel>;
  showBanners: (banners?: Array<IBannerModel>, paymentsEncodedLeaseId?: string, rentTermsKey?: string) => void;
  fromEmail?: boolean;
  landlordStatus?: string;
  showHostedOnboardingRequiredNotification: () => any;
  showHostedOnboardingInformationReceivedNotification: () => any;
  showInviteNewRenterNotification: (
    currentTerm,
    rentTermsExtendPermissionCurrentTerm,
    leasesForProperty,
    isRentGuaranteeEnabled,
    listingAlias,
  ) => void;
  shouldInviteNewRenter?: boolean;
  rentTermsKey?: string;
  rentTermsExtendPermissionCurrentTerm?: IFeaturePermissionObject;
  rentTermsExtendPermissionNextTerm?: IFeaturePermissionObject;
  currentTerm?: any;
  baseUrl?: string;
  propertyId?: string;
  listingAlias?: string;
  nextTerm?: any;
  tenantFirstName?: string;
  dismissibleActions?: any;
  acknowledgeRolloutModal?: any;
  showRentalProtectionFirstRentPaymentFailedNotification: (tenantEmail: string) => void;
  showRentalProtectionLatestRentPaymentFailedNotification: (tenantEmail: string) => void;
  showRentalProtectionDocumentNotSignedNotification: (listingAlias, policyId) => void;
  showRentalProtectionPolicyPendingNotification: () => void;
  showRentalProtectionMissedRentNotification: () => void;
  isRentGuaranteeEnabled: boolean;
  isRentalProtectionVariation: boolean;
  isLatestRentPaymentFailed: boolean;
  shouldDisplayRentalProtectionFirstRentFailedBanner: boolean;
  shouldDisplayRentalProtectionMissedRentBanner: boolean;
  paymentsEncodedLeaseId?: string;
  leasesForProperty: Array<LeaseSelectorProps>;
  tenantEmail: string;
  shouldShowHostedOnboardingInformationReceivedNotification: boolean;
  showFastPaymentsBannerNotification: () => void;
};
type State = {
  showRolloutModal: boolean;
};

class SinglePropertyDashboard extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      showRolloutModal: props.fromEmail
        ? false
        : Boolean(props.dismissibleActions[DismissibleActionConstants.SCHEDULING_ROLLOUT_LANDLORD]),
    };
  }

  componentDidMount(): any {
    const {
      banners,
      showBanners,
      rentTermsKey,
      paymentsEncodedLeaseId,
      listingAlias,
      fromEmail,
      landlordStatus,
      shouldInviteNewRenter,
      showHostedOnboardingRequiredNotification,
      showHostedOnboardingInformationReceivedNotification,
      showInviteNewRenterNotification,
      showFastPaymentsBannerNotification,
      currentTerm,
      rentTermsExtendPermissionCurrentTerm,
      showRentalProtectionFirstRentPaymentFailedNotification,
      showRentalProtectionLatestRentPaymentFailedNotification,
      showRentalProtectionDocumentNotSignedNotification,
      showRentalProtectionPolicyPendingNotification,
      isRentGuaranteeEnabled,
      isRentalProtectionVariation,
      isLatestRentPaymentFailed,
      shouldDisplayRentalProtectionFirstRentFailedBanner,
      showRentalProtectionMissedRentNotification,
      tenantEmail,
      shouldDisplayRentalProtectionMissedRentBanner,
      leasesForProperty,
      shouldShowHostedOnboardingInformationReceivedNotification,
    } = this.props;

    showFastPaymentsBannerNotification();

    if (fromEmail && this.props.dismissibleActions[DismissibleActionConstants.SCHEDULING_ROLLOUT_LANDLORD]) {
      this.props.acknowledgeRolloutModal();
    }

    if (banners) {
      showBanners(banners, paymentsEncodedLeaseId, rentTermsKey);
    }

    if (landlordStatus === LANDLORD_STATUS.ADDITIONAL_VERIFICATION_NEEDED) {
      if (!shouldShowHostedOnboardingInformationReceivedNotification) {
        showHostedOnboardingRequiredNotification();
      }
    }
    if (shouldShowHostedOnboardingInformationReceivedNotification) {
      showHostedOnboardingInformationReceivedNotification();
    }
    if (shouldInviteNewRenter) {
      showInviteNewRenterNotification(
        currentTerm,
        rentTermsExtendPermissionCurrentTerm,
        leasesForProperty,
        isRentGuaranteeEnabled,
        listingAlias,
      );
    }

    // for now only show banners for current term - need to discuss how to handle nextTerm
    const { rentalProtectionPolicy } = currentTerm;
    if (isRentalProtectionVariation && rentalProtectionPolicy) {
      const { policyStatus, coverageRequestSigned, policyId } = rentalProtectionPolicy;
      if (!coverageRequestSigned) {
        showRentalProtectionDocumentNotSignedNotification(listingAlias, policyId);
      } else if (shouldDisplayRentalProtectionFirstRentFailedBanner) {
        showRentalProtectionFirstRentPaymentFailedNotification(tenantEmail);
      } else if (isLatestRentPaymentFailed) {
        showRentalProtectionLatestRentPaymentFailedNotification(tenantEmail);
      } else if (
        !isLatestRentPaymentFailed &&
        !shouldDisplayRentalProtectionFirstRentFailedBanner &&
        policyStatus === COVERAGE_REQUEST_SIGNED
      ) {
        showRentalProtectionPolicyPendingNotification();
      } else if (shouldDisplayRentalProtectionMissedRentBanner) {
        showRentalProtectionMissedRentNotification();
      }
    }
  }

  acknowledgeRolloutModal = (): void => {
    this.setState({ showRolloutModal: false });
    this.props.acknowledgeRolloutModal(
      this.props.dismissibleActions[DismissibleActionConstants.SCHEDULING_ROLLOUT_LANDLORD],
    );
  };

  render(): React.ReactNode {
    const {
      baseUrl,
      propertyId,
      currentTerm,
      nextTerm,
      allTransactions,
      tenantFirstName,
      rentTermsExtendPermissionCurrentTerm,
      rentTermsExtendPermissionNextTerm,
      shouldDisplayRentalProtectionMissedRentBanner,
    } = this.props;
    const { showRolloutModal } = this.state;

    return (
      <Page persona={userPersonas.LANDLORD} dataAutoTestId="landlord-dashboard-single">
        <Container fluid fluidMobile className="SinglePropertyDashboard-main phn">
          {showRolloutModal && (
            <RolloutModal onSubmit={this.acknowledgeRolloutModal} tenantFirstName={tenantFirstName} />
          )}
          <LedgerSection
            baseUrl={baseUrl}
            propertyId={propertyId}
            currentTerm={currentTerm}
            nextTerm={nextTerm}
            allTransactions={allTransactions}
            shouldDisplayRentalProtectionMissedRentBanner={shouldDisplayRentalProtectionMissedRentBanner}
          />
          <InfoSection
            baseUrl={baseUrl}
            propertyId={propertyId}
            currentTerm={currentTerm}
            nextTerm={nextTerm}
            rentTermsExtendPermissionCurrentTerm={rentTermsExtendPermissionCurrentTerm}
            rentTermsExtendPermissionNextTerm={rentTermsExtendPermissionNextTerm}
          />
        </Container>
      </Page>
    );
  }
}
export default SinglePropertyDashboard;
